import { supabase } from '../lib/supabase';
import { Break, BreakWithPlayer, BreakStats, BreakFilters } from '../types/breaks';

// Create a new break (using updated schema)
export const createBreak = async (breakData: {
  frameId: number;
  gameId: number;
  player: string;
  sequence: number[]; // JSON array of ball values
  score: number;
  isFoul: boolean;
  foulBall?: number;
  foulPoints: number;
  breakEnded: boolean;
  redsRemaining: number;
  timestamp: Date;
}): Promise<Break> => {
  console.log('Creating break with data:', {
    frameId: breakData.frameId,
    gameId: breakData.gameId,
    player: breakData.player,
    sequence: breakData.sequence,
    score: breakData.score,
    isFoul: breakData.isFoul,
    foulBall: breakData.foulBall,
    foulPoints: breakData.foulPoints,
    breakEnded: breakData.breakEnded,
    redsRemaining: breakData.redsRemaining,
    timestamp: breakData.timestamp.toISOString()
  });

  const { data, error } = await supabase
    .from('breaks')
    .insert([{
      frameId: breakData.frameId,
      gameId: breakData.gameId,
      player: breakData.player,
      sequence: breakData.sequence, // JSON array
      score: breakData.score,
      isFoul: breakData.isFoul,
      foulBall: breakData.foulBall,
      foulPoints: breakData.foulPoints,
      breakEnded: breakData.breakEnded,
      redsRemaining: breakData.redsRemaining,
      timestamp: breakData.timestamp.toISOString()
    }])
    .select()
    .single();

  if (error) {
    console.error('Database error:', error);
    throw new Error(`Failed to create break: ${error.message}`);
  }

  return {
    ...data,
    sequence: data.sequence || [],
    timestamp: new Date(data.timestamp),
  };
};

// Get breaks for a specific game
export const getBreaksByGame = async (gameId: number): Promise<BreakWithPlayer[]> => {
  const { data, error } = await supabase
    .from('breaks')
    .select(`
      *,
      games!breaks_gameId_fkey (
        playerA:players!games_playerA_fkey (name, preName),
        playerB:players!games_playerB_fkey (name, preName)
      )
    `)
    .eq('gameId', gameId)
    .order('timestamp', { ascending: true });

  if (error) {
    throw new Error(`Failed to get breaks: ${error.message}`);
  }

  return (data || []).map(breakItem => ({
    ...breakItem,
    sequence: breakItem.sequence || [],
    timestamp: new Date(breakItem.timestamp),
    playerName: breakItem.player === 'A' 
      ? breakItem.games.playerA.name 
      : breakItem.games.playerB.name,
    playerPreName: breakItem.player === 'A' 
      ? breakItem.games.playerA.preName 
      : breakItem.games.playerB.preName,
  }));
};

// Get breaks for a specific frame (simple version for state recreation)
export const getBreaksByFrame = async (frameId: number): Promise<Break[]> => {
  const { data, error } = await supabase
    .from('breaks')
    .select('*')
    .eq('frameId', frameId)
    .order('timestamp', { ascending: true });

  if (error) {
    throw new Error(`Failed to get breaks: ${error.message}`);
  }

  return (data || []).map(breakItem => ({
    ...breakItem,
    sequence: breakItem.sequence || [],
    timestamp: new Date(breakItem.timestamp),
  }));
};

// Get breaks for a specific frame with player details
export const getBreaksByFrameWithPlayers = async (frameId: number): Promise<BreakWithPlayer[]> => {
  const { data, error } = await supabase
    .from('breaks')
    .select(`
      *,
      frames!breaks_frameId_fkey (
        games!frames_gameId_fkey (
          playerA:players!games_playerA_fkey (name, preName),
          playerB:players!games_playerB_fkey (name, preName)
        )
      )
    `)
    .eq('frameId', frameId)
    .order('timestamp', { ascending: true });

  if (error) {
    throw new Error(`Failed to get breaks: ${error.message}`);
  }

  return (data || []).map(breakItem => ({
    ...breakItem,
    sequence: breakItem.sequence || [],
    timestamp: new Date(breakItem.timestamp),
    playerName: breakItem.player === 'A' 
      ? breakItem.frames.games.playerA.name 
      : breakItem.frames.games.playerB.name,
    playerPreName: breakItem.player === 'A' 
      ? breakItem.frames.games.playerA.preName 
      : breakItem.frames.games.playerB.preName,
  }));
};

// Get break statistics for a player
export const getBreakStats = async (playerId: number, gameId?: number): Promise<BreakStats> => {
  let query = supabase
    .from('breaks')
    .select('score, isFoul')
    .eq('player', playerId);

  if (gameId) {
    query = query.eq('gameId', gameId);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Failed to get break stats: ${error.message}`);
  }

  const breaks = data || [];
  const validBreaks = breaks.filter(b => !b.isFoul && b.score > 0);
  
  const scores = validBreaks.map(b => b.score);
  const totalBreaks = validBreaks.length;
  const highestBreak = Math.max(...scores, 0);
  const averageBreak = totalBreaks > 0 ? scores.reduce((a, b) => a + b, 0) / totalBreaks : 0;
  const centuries = scores.filter(s => s >= 100).length;
  const halfCenturies = scores.filter(s => s >= 50 && s < 100).length;

  return {
    highestBreak,
    totalBreaks,
    averageBreak: Math.round(averageBreak * 100) / 100,
    centuries,
    halfCenturies,
  };
};

// Update a break (for ongoing breaks)
export const updateBreak = async (id: number, updates: Partial<Break>): Promise<Break> => {
  const { data, error } = await supabase
    .from('breaks')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update break: ${error.message}`);
  }

  return {
    ...data,
    sequence: data.sequence || [],
    timestamp: new Date(data.timestamp),
  };
};

// Delete a break
export const deleteBreak = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from('breaks')
    .delete()
    .eq('id', id);

  if (error) {
    throw new Error(`Failed to delete break: ${error.message}`);
  }
};

// Get breaks with filters
export const getBreaksWithFilters = async (filters: BreakFilters): Promise<BreakWithPlayer[]> => {
  let query = supabase
    .from('breaks')
    .select(`
      *,
      games!breaks_gameId_fkey (
        playerA:players!games_playerA_fkey (name, preName),
        playerB:players!games_playerB_fkey (name, preName)
      )
    `);

  // Apply filters
  if (filters.player) {
    query = query.eq('player', filters.player);
  }
  if (filters.gameId) {
    query = query.eq('gameId', filters.gameId);
  }
  if (filters.frameId) {
    query = query.eq('frameId', filters.frameId);
  }
  if (filters.minScore !== undefined) {
    query = query.gte('score', filters.minScore);
  }
  if (filters.maxScore !== undefined) {
    query = query.lte('score', filters.maxScore);
  }
  if (filters.isFoul !== undefined) {
    query = query.eq('isFoul', filters.isFoul);
  }
  if (filters.dateFrom) {
    query = query.gte('timestamp', filters.dateFrom.toISOString());
  }
  if (filters.dateTo) {
    query = query.lte('timestamp', filters.dateTo.toISOString());
  }

  const { data, error } = await query.order('timestamp', { ascending: false });

  if (error) {
    throw new Error(`Failed to get filtered breaks: ${error.message}`);
  }

  return (data || []).map(breakItem => ({
    ...breakItem,
    sequence: breakItem.sequence || [],
    timestamp: new Date(breakItem.timestamp),
    playerName: breakItem.player === 'A' 
      ? breakItem.games.playerA.name 
      : breakItem.games.playerB.name,
    playerPreName: breakItem.player === 'A' 
      ? breakItem.games.playerA.preName 
      : breakItem.games.playerB.preName,
  }));
};

// Helper function to calculate break score from sequence
export const calculateBreakScore = (sequence: number[]): number => {
  return sequence.reduce((total, ball) => {
    const ballValue = Math.abs(ball);
    const isFoul = ball < 0;
    
    if (isFoul) {
      // Foul points: balls 1-4 count as 4, others count as their value
      return total + (ballValue <= 4 ? 4 : ballValue);
    } else {
      return total + ballValue;
    }
  }, 0);
};

// Helper function to determine if break has foul
export const hasFoul = (sequence: number[]): boolean => {
  return sequence.some(ball => ball < 0);
};

// Helper function to get foul ball from sequence
export const getFoulBall = (sequence: number[]): number | undefined => {
  const foulBall = sequence.find(ball => ball < 0);
  return foulBall ? Math.abs(foulBall) : undefined;
};

// Helper function to calculate foul points
export const calculateFoulPoints = (foulBall: number): number => {
  return foulBall <= 4 ? 4 : foulBall;
};
